import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../i18n';
import { api } from '../api/client';
import Turnstile, { type TurnstileStatus } from '../components/Turnstile';

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';

/**
 * 忘记密码页 /forgot-password
 *
 * 输入邮箱发送重置邮件。无论邮箱是否注册都显示相同提示
 * （后端不泄露注册状态）。发送后提示检查收件箱（含垃圾邮件）。
 */
export default function ForgotPassword() {
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [tsToken, setTsToken] = useState('');
  const [tsKey, setTsKey] = useState(0);
  const [tsStatus, setTsStatus] = useState<TurnstileStatus>('loading');

  const captchaRequired = !!TURNSTILE_SITE_KEY;

  async function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError('');
    if (!email.includes('@')) {
      setError(t('auth.errEmail'));
      return;
    }
    setSubmitting(true);
    try {
      await api.forgotPassword(email, tsToken || undefined);
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      // token 已被服务端消费，重挂载 widget 获取新 token
      setTsToken('');
      setTsStatus('loading');
      setTsKey((k) => k + 1);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1 className="auth-title">{t('auth.forgotTitle')}</h1>
        <p className="auth-subtitle">{t('auth.forgotSub')}</p>

        {sent ? (
          <div className="state-box" style={{ borderColor: 'var(--ok, #16a34a)' }}>
            <p style={{ margin: 0, lineHeight: 1.7 }}>{t('auth.forgotSent')}</p>
          </div>
        ) : (
          <form className="auth-form" onSubmit={handleSubmit} noValidate>
            <div className="form-group">
              <label className="form-label" htmlFor="fp-email">
                {t('auth.email')}
              </label>
              <input
                id="fp-email"
                className="form-input"
                type="email"
                placeholder="you@example.com"
                value={email}
                autoComplete="email"
                disabled={submitting}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            {captchaRequired && (
              <Turnstile
                key={tsKey}
                siteKey={TURNSTILE_SITE_KEY}
                onToken={setTsToken}
                onStatus={setTsStatus}
              />
            )}
            {captchaRequired && !tsToken && (
              <div className="form-hint" style={{ marginTop: '-0.5rem', marginBottom: '0.75rem' }}>
                {tsStatus === 'timeout' ? t('auth.captchaSlow') : t('auth.captchaPreparing')}
              </div>
            )}
            {error && <div className="state-box state-error">{error}</div>}
            <button
              className="btn btn-primary btn-lg auth-submit"
              type="submit"
              disabled={submitting || (captchaRequired && !tsToken)}
            >
              {submitting ? t('auth.submitting') : t('auth.forgotSubmit')}
            </button>
          </form>
        )}

        <p style={{ textAlign: 'center', marginTop: '1.25rem', fontSize: '0.85rem' }}>
          <Link to="/login" style={{ color: 'var(--accent)' }}>
            {t('auth.backToLogin')}
          </Link>
        </p>
      </div>
    </div>
  );
}
